import { Form } from 'antd';
import { FC, ReactElement, memo, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError, parseDate } from '@/common/utils';

import { DomainDeployStatus, DomainFormFields, DomainFormInputs, DomainsQueryKeys, useEditDomainsMutation, useGetByIdDomainsQuery } from '@/modules/Domains';

import { DomainsFormView } from './DomainsFormView';
import { domainFormValidationSchema } from './validation';

interface Props {
  domainId: string;
}

export const DomainsEditFormContainer: FC<Props> = memo(({ domainId }) => {
  const queryClients = useQueryClient();
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DomainFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainFormInputs>>(null);

  const domainRegistrarFormId = Form.useWatch<string | undefined>(
    [DomainFormInputs.DomainRegistrarId],
    form
  );

  const {
    data: domainData,
    error: domainError,
    isLoading: isDomainLoading,
    isError: isDomainError
  } = useGetByIdDomainsQuery(domainId)({
    enabled: !!domainId,
    onSuccess: (data) => {
      form.setFieldsValue({
        names: [data.name],
        domainRegistrarId: data.domainRegistrar.id,
        domainRegistrarAccountId: data.domainRegistrarAccount.id,
        registratedAt: data.registratedAt ? parseDate(data.registratedAt) : undefined,
        paidAt: data.paidAt ? parseDate(data.paidAt) : undefined
      });
    }
  });

  const { mutate: editDomainMutation, isLoading: editDomainIsLoading } = useEditDomainsMutation(domainId)({
    onSuccess: async (data) => {
      appMessageApi.success('Домен успішно змінено!');

      await queryClients.refetchQueries([DomainsQueryKeys.GetAll]);

      navigate(AppRoutes.DomainsById.replace(':domainId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof DomainFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: DomainFormFields = {
    names: [domainData?.name],
    domainRegistrarId: domainData?.domainRegistrar.id,
    domainRegistrarAccountId: domainData?.domainRegistrarAccount.id,
    registratedAt: domainData?.registratedAt ? parseDate(domainData?.registratedAt) : undefined,
    paidAt: domainData?.paidAt ? parseDate(domainData?.paidAt) : undefined
  };

  const cannotBeUpdatedCauseStatus = useMemo(
    () =>
      domainData?.isDeployed ||
      domainData?.deployStatus === DomainDeployStatus.Deploying ||
      domainData?.deployStatus === DomainDeployStatus.Pending,
    [domainData?.isDeployed, domainData?.deployStatus]
  );

  const handleFinish = useCallback(
    (values: DomainFormFields): void => {
      setFormErrors(null);

      const { registratedAt, paidAt } = values;

      editDomainMutation(cannotBeUpdatedCauseStatus ? { registratedAt, paidAt } : values);
    },
    [domainData]
  );

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.DomainsById.replace(':domainId', domainId));
  }, [domainId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout isLoading={ isDomainLoading } isError={ isDomainError } error={ domainError }>
      { (): ReactElement => (
        <DomainsFormView
          formName="domain-edit"
          formType="edit"
          form={ form }
          formErrors={ formErrors }
          initialValues={ initialValues }
          isFormLoading={ editDomainIsLoading }
          validationSchema={ domainFormValidationSchema }
          cannotBeUpdatedCauseStatus={ cannotBeUpdatedCauseStatus }
          domainRegistrarFormId={ domainRegistrarFormId }
          domainsMode={ null }
          handleChangeDomainsMode={ () => {} }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      ) }
    </FallbackedLayout>
  );
});
